/**
 * The named Ember humanoids: what `/ember appearance <name>` puts a player into.
 *
 * Each is a record of data -- a look and optional proportions -- and every frame of every animation
 * is generated from it. Adding a character is adding an entry to content
 * (`content/game/sprites/ember/humanoids.json`), with the builds they share and which one draws
 * each race. Bound into the records here in place, so every importer sees content's.
 */
import { bindContent } from "../content/catalog"
import type { EmberLook } from "./body"
import { noteEmberContent } from "./model"
import type { Proportions } from "./rig"

export interface EmberHumanoid {
    name: string
    about?: string
    look: EmberLook
    proportions?: Partial<Proportions>
    /** An EMBER_STYLES name (styles.ts); LPC's by default. */
    style?: string
    /** How it carries itself over every clip: `shamble`, a zombie's (clips.ts shambling). */
    posture?: "shamble"
    /** The smallest frame it is drawn on: a body taller than the 64px frame allows needs more room. */
    frame?: number
}

/** A build as content writes it: proportions over another build's, the whole scaled by `scale`. */
export interface EmberBuild {
    about?: string
    extends?: string
    proportions?: Partial<Proportions>
    scale?: number
}
/** A humanoid as content writes it: its build by name, its own proportions over that (or the default). */
export interface EmberHumanoidSource extends Omit<EmberHumanoid, "proportions"> {
    build?: string
    proportions?: Partial<Proportions>
}
/** The first rule an entity's race (and sex, where the rule names one) matches picks its body. */
export interface EmberRaceRule {
    race: string
    sex?: string
    body: string
}
/**
 * How an entity's body colour (appearance.bodyColor, the game's LPC body colours) recolours its
 * Ember body: a skeleton's bone takes it as is (palettes.json bones); a living human's skin -- a look
 * whose skin is one of `skins` -- becomes the skin `skins` names for it; a beast's fur, the skin its
 * beast's `furs` table names. A colour none names leaves the look's own.
 */
export interface EmberBodyColors {
    skins: Record<string, string>
    furs: Record<string, Record<string, string>>
}
export interface EmberHumanoidFile {
    version: 1
    about?: string
    builds: Record<string, EmberBuild>
    humanoids: EmberHumanoidSource[]
    races: EmberRaceRule[]
    bodyColors: EmberBodyColors & { about?: string }
}

export const EMBER_HUMANOIDS: Record<string, EmberHumanoid> = {}
export const EMBER_BODY_COLORS: EmberBodyColors = { skins: {}, furs: {} }
/** Their names, in content's order. */
export const EMBER_HUMANOID_NAMES: string[] = []
const RACES: EmberRaceRule[] = []

/**
 * A build scaled whole: every length and girth by `k` -- not its roundness, cloth or stance angles --
 * so the same body comes out larger or smaller.
 */
const scaledBy = (p: Partial<Proportions>, k: number): Partial<Proportions> => {
    const unscaled = new Set(["headRoundness", "torsoRoundness", "pelvisRoundness", "clothThickness"])
    const out: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(p)) {
        if (unscaled.has(key)) out[key] = value
        else if (typeof value === "number") out[key] = value * k
        else if (Array.isArray(value)) out[key] = value.map((v: number) => v * k)
        else if (key === "girdle") {
            const girdle = value as Proportions["girdle"]
            out[key] = { ...girdle, radii: girdle.radii.map(v => v * k), drop: girdle.drop * k, cut: girdle.cut * k }
        } else if (key === "stance") {
            const scaledPose = (pose: Record<string, number>) => ({
                ...pose,
                ...(pose.feetOut !== undefined ? { feetOut: pose.feetOut * k } : {}),
                ...(pose.feetSplit !== undefined ? { feetSplit: pose.feetSplit * k } : {}),
            })
            out[key] = Object.fromEntries(Object.entries(value as object).map(([facing, pose]) => [facing, scaledPose(pose)]))
        } else out[key] = value
    }
    return out as Partial<Proportions>
}

/** Every build's proportions, resolved through what it extends; humanoids of one build share one object. */
export const resolveBuilds = (builds: Record<string, EmberBuild>): Record<string, Partial<Proportions>> => {
    const done: Record<string, Partial<Proportions>> = {}
    const resolve = (name: string, seen: string[]): Partial<Proportions> => {
        if (done[name]) return done[name]
        const b = builds[name]
        if (!b) throw new Error(`Unknown build ${name}`)
        if (seen.includes(name)) throw new Error(`Build ${name} extends itself (${[...seen, name].join(" -> ")})`)
        const own = { ...(b.extends ? resolve(b.extends, [...seen, name]) : {}), ...(b.proportions ?? {}) }
        return (done[name] = b.scale !== undefined ? scaledBy(own, b.scale) : own)
    }
    for (const name of Object.keys(builds)) resolve(name, [])
    return done
}

/** Fill the humanoids from content's humanoids.json (bound below). */
export const registerHumanoids = (file: EmberHumanoidFile | undefined): void => {
    const builds = resolveBuilds(file?.builds ?? {})
    for (const k of Object.keys(EMBER_HUMANOIDS)) delete EMBER_HUMANOIDS[k]
    EMBER_HUMANOID_NAMES.length = 0
    for (const { build, proportions, ...h } of file?.humanoids ?? []) {
        const base = build ? builds[build] : undefined
        EMBER_HUMANOIDS[h.name] = { ...h, ...(base || proportions ? { proportions: base && proportions ? { ...base, ...proportions } : base ?? proportions } : {}) }
        EMBER_HUMANOID_NAMES.push(h.name)
    }
    RACES.length = 0
    RACES.push(...(file?.races ?? []))
    EMBER_BODY_COLORS.skins = { ...(file?.bodyColors?.skins ?? {}) }
    EMBER_BODY_COLORS.furs = { ...(file?.bodyColors?.furs ?? {}) }
    noteEmberContent("humanoids", file)
}

export const isEmberHumanoid = (name: string | undefined): boolean => !!name && Object.prototype.hasOwnProperty.call(EMBER_HUMANOIDS, name)

/** An appearance naming this is drawn with its LPC body even where an Ember one exists. */
export const LPC_BODY = "lpc"

/**
 * The Ember humanoid an entity is drawn as: the one its appearance names, else the one for its
 * race (content's race rules: the realist builds for a human man or woman -- clothes, hair and skin
 * come from the entity -- the skeleton, the zombie, the wolfman) -- or none, its LPC body, where
 * Ember has no body for its race yet or where it asked for LPC.
 */
export const emberSpriteFor = (appearance?: { emberSprite?: string; race?: string; sex?: string } | null): string | undefined => {
    if (!appearance || appearance.emberSprite === LPC_BODY) return undefined
    if (isEmberHumanoid(appearance.emberSprite)) return appearance.emberSprite
    const rule = RACES.find(r => r.race === appearance.race && (r.sex === undefined || r.sex === appearance.sex))
    return rule && isEmberHumanoid(rule.body) ? rule.body : undefined
}

bindContent(catalog => (catalog.source as any).emberHumanoids, registerHumanoids)
